/**
 * Overall-experience feedback widget for a catalog video: star rating + reaction
 * chips + optional comment. Posts to POST /api/learn/:shareToken/feedback with an
 * anonymous reviewerKey. Distinct from precise review mode (timestamped comments).
 */

import { useState } from 'react';
import { Star, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { REACTIONS, getReviewerKey } from './learnShared';
import { submitExperienceFeedback } from '../../services/learnAPI';

export function ExperienceFeedbackWidget({
  shareToken,
  averageRating,
  ratingCount,
}: {
  shareToken: string;
  averageRating?: number | null;
  ratingCount?: number;
}) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reactions, setReactions] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agg, setAgg] = useState<{ avg: number | null; count: number }>({
    avg: averageRating ?? null,
    count: ratingCount ?? 0,
  });

  const toggleReaction = (k: string) =>
    setReactions((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  async function submit() {
    if (rating < 1) { setError('Please pick a star rating.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await submitExperienceFeedback(shareToken, {
        rating,
        reactions: Array.from(reactions),
        comment: comment.trim() || null,
        reviewerKey: getReviewerKey(),
      });
      setAgg({ avg: res.averageRating, count: res.ratingCount });
      setDone(true);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to submit feedback');
    } finally {
      setBusy(false);
    }
  }

  const litTo = hover || rating;

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-baseline justify-between">
        <h3 className="font-serif text-[15px] font-semibold">Rate this experience</h3>
        {agg.count > 0 && (
          <span className="text-xs font-medium text-[#8A8593]">
            {agg.avg?.toFixed(1)} ★ · {agg.count} {agg.count === 1 ? 'rating' : 'ratings'}
          </span>
        )}
      </div>

      {done ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm font-medium text-[#23766A] dark:text-[#5FC7B6]">
            <Check className="h-4 w-4" /> Thanks — your feedback was recorded.
          </span>
          <button
            type="button"
            onClick={() => {
              setRating(0);
              setReactions(new Set());
              setComment('');
              setDone(false);
            }}
            className="text-[13px] font-semibold text-[#6D28D9] hover:underline dark:text-[#B69BF5]"
          >
            Rate again
          </button>
        </div>
      ) : (
        <>
          {/* Stars */}
          <div className="mt-3 flex gap-1.5" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                type="button"
                aria-label={`${v} star${v > 1 ? 's' : ''}`}
                onMouseEnter={() => setHover(v)}
                onClick={() => setRating(v)}
                className="transition-transform hover:scale-110"
              >
                <Star className={cn('h-7 w-7', v <= litTo ? 'fill-[#E5A93C] text-[#E5A93C]' : 'text-black/20 dark:text-white/20')} />
              </button>
            ))}
          </div>

          {/* Reaction chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            {REACTIONS.map((r) => {
              const on = reactions.has(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => toggleReaction(r.key)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
                    on
                      ? 'border-[#7C3AED]/50 bg-[#7C3AED]/10 text-[#6D28D9] dark:text-[#B69BF5]'
                      : 'border-black/10 bg-black/[0.02] text-[#56505F] hover:border-black/20 dark:border-white/10 dark:bg-white/[0.04] dark:text-[#B2B0C0] dark:hover:border-white/20',
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Comment */}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What worked? What was confusing? (optional)"
            className="mt-4 min-h-[64px] w-full resize-y rounded-xl border border-black/10 bg-[#FAF8F4] px-3 py-2.5 text-[13px] outline-none focus:border-[#7C3AED] dark:border-white/10 dark:bg-[#0C0C12]"
          />

          {error && <p className="mt-2 text-xs font-medium text-[#b65187]">{error}</p>}

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="mt-3 w-full rounded-xl bg-[#6D28D9] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#7C3AED] disabled:opacity-60"
          >
            {busy ? 'Submitting…' : 'Submit feedback'}
          </button>
        </>
      )}
    </div>
  );
}
